import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Users,
  Shield,
  User,
  Trash2,
  Plus,
  Edit,
  Lock,
  Mail,
  Clock,
  Save,
  Calendar
} from 'lucide-react';
import { getUsers, createUser, updateUser, deleteUser, getSettings, updateSettings } from '../services/api';
import { toast } from 'sonner';

// Day configuration
const DAYS = [
  { key: 'monday', label: 'Luni' },
  { key: 'tuesday', label: 'Marți' },
  { key: 'wednesday', label: 'Miercuri' },
  { key: 'thursday', label: 'Joi' },
  { key: 'friday', label: 'Vineri' },
];

// Generate time options (30 min intervals, 06:00 - 22:00)
const TIME_OPTIONS = [];
for (let hour = 6; hour <= 22; hour++) {
  for (const minute of [0, 30]) {
    if (hour === 22 && minute === 30) continue;
    TIME_OPTIONS.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
  }
}

const DEFAULT_SCHEDULE = {
  monday:    { start_time: '08:00', end_time: '20:00' },
  tuesday:   { start_time: '08:00', end_time: '20:00' },
  wednesday: { start_time: '08:00', end_time: '20:00' },
  thursday:  { start_time: '08:00', end_time: '20:00' },
  friday:    { start_time: '08:00', end_time: '20:00' },
};

// ---- Day Schedule Row Component ----
const DayScheduleRow = ({ day, schedule, onChange }) => {
  const startIdx = TIME_OPTIONS.indexOf(schedule.start_time);
  const endIdx = TIME_OPTIONS.indexOf(schedule.end_time);

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 py-3 border-b border-slate-100 last:border-0" data-testid={`schedule-row-${day.key}`}>
      <div className="w-24 shrink-0">
        <span className="font-semibold text-slate-700 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-emerald-500 hidden sm:block" />
          {day.label}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-1 w-full sm:w-auto">
        <Select value={schedule.start_time} onValueChange={(v) => onChange(day.key, 'start_time', v)}>
          <SelectTrigger className="w-[110px]" data-testid={`${day.key}-start-time`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TIME_OPTIONS.map((t, i) => (
              <SelectItem key={t} value={t} disabled={i >= endIdx}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-slate-400 text-sm">-</span>
        <Select value={schedule.end_time} onValueChange={(v) => onChange(day.key, 'end_time', v)}>
          <SelectTrigger className="w-[110px]" data-testid={`${day.key}-end-time`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TIME_OPTIONS.map((t, i) => (
              <SelectItem key={t} value={t} disabled={i <= startIdx}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

const SettingsPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  // Weekly schedule state
  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
  const [savingSettings, setSavingSettings] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'medic'
  });

  const [editFormData, setEditFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    role: ''
  });

  useEffect(() => {
    fetchSettings();
    if (user?.role === 'admin') {
      fetchUsers();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchSettings = async () => {
    try {
      const response = await getSettings();
      if (response.data?.schedule) {
        const s = response.data.schedule;
        setSchedule({
          monday:    s.monday    || DEFAULT_SCHEDULE.monday,
          tuesday:   s.tuesday   || DEFAULT_SCHEDULE.tuesday,
          wednesday: s.wednesday || DEFAULT_SCHEDULE.wednesday,
          thursday:  s.thursday  || DEFAULT_SCHEDULE.thursday,
          friday:    s.friday    || DEFAULT_SCHEDULE.friday,
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleScheduleChange = (dayKey, field, value) => {
    setSchedule(prev => ({
      ...prev,
      [dayKey]: { ...prev[dayKey], [field]: value }
    }));
  };

  const handleSaveSchedule = async () => {
    // Validate each day
    for (const day of DAYS) {
      const d = schedule[day.key];
      const [sh, sm] = d.start_time.split(':').map(Number);
      const [eh, em] = d.end_time.split(':').map(Number);
      if (eh * 60 + em <= sh * 60 + sm) {
        toast.error(`${day.label}: Ora de sfârșit trebuie să fie după ora de început`);
        return;
      }
      if ((eh * 60 + em) - (sh * 60 + sm) < 60) {
        toast.error(`${day.label}: Intervalul minim este de 1 oră`);
        return;
      }
    }
    
    setSavingSettings(true);
    try {
      const payload = {};
      for (const day of DAYS) {
        payload[day.key] = {
          enabled: true,
          start_time: schedule[day.key].start_time,
          end_time: schedule[day.key].end_time
        };
      }
      await updateSettings(payload);
      toast.success('Programul a fost salvat cu succes');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la salvarea programului');
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await getUsers();
      setUsers(response.data);
    } catch (error) {
      toast.error('Eroare la încărcarea utilizatorilor');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await createUser(formData);
      toast.success(`Utilizatorul ${formData.full_name} a fost creat!`);
      setCreateDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la crearea utilizatorului');
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        email: editFormData.email,
        full_name: editFormData.full_name,
        role: editFormData.role
      };
      // Adaugă parola doar dacă a fost completată
      if (editFormData.password) {
        updateData.password = editFormData.password;
      }
      
      await updateUser(editingUser.id, updateData);
      toast.success(`Utilizatorul ${editFormData.full_name} a fost actualizat!`);
      setEditDialogOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la actualizarea utilizatorului');
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await deleteUser(userToDelete.id);
      toast.success(`Utilizatorul ${userToDelete.full_name} a fost șters`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la ștergerea utilizatorului');
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const openEditDialog = (u) => {
    setEditingUser(u);
    setEditFormData({
      email: u.email,
      full_name: u.full_name,
      password: '',
      role: u.role
    });
    setEditDialogOpen(true);
  };

  const confirmDelete = (u) => {
    setUserToDelete(u);
    setDeleteDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      full_name: '',
      password: '',
      role: 'medic'
    });
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-700';
      case 'medic':
        return 'bg-emerald-100 text-emerald-700';
      case 'asistent':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin':
        return 'Administrator';
      case 'medic':
        return 'Medic';
      case 'asistent':
        return 'Asistent';
      default:
        return role;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user?.role !== 'admin' && user?.role !== 'medic') {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="settings-page">
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Setări</h1>
        <Card>
          <CardContent className="p-8 text-center">
            <Shield className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-lg text-slate-600">Acces restricționat</p>
            <p className="text-sm text-slate-500 mt-1">Doar medicul sau administratorul pot accesa această pagină</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Setări</h1>
        <p className="text-slate-500 mt-1">Administrare program consultații{user?.role === 'admin' && ' și utilizatori'}</p>
      </div>

      {/* Weekly Schedule Settings - Available to medic and admin */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Clock className="w-5 h-5 text-emerald-500" />
            Program Consultații
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-slate-500 mb-4">
            Setați programul de consultații pentru fiecare zi lucrătoare. Programările sunt la intervale de <span className="font-semibold">30 minute</span>.
          </p>
          <div className="space-y-0">
            {DAYS.map(day => (
              <DayScheduleRow
                key={day.key}
                day={day}
                schedule={schedule[day.key]}
                onChange={handleScheduleChange}
              />
            ))}
          </div>
          <div className="flex justify-end pt-4">
            <Button 
              onClick={handleSaveSchedule}
              disabled={savingSettings}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="save-schedule-btn"
            >
              {savingSettings ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salvează Programul
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Users Management - Admin only */}
      {user?.role === 'admin' && (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-xl">
            <Users className="w-5 h-5 text-emerald-500" />
            Utilizatori ({users.length})
          </CardTitle>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-500 hover:bg-emerald-600" data-testid="add-user-btn">
                <Plus className="w-4 h-4 mr-2" />
                Adaugă Utilizator
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Creează Utilizator Nou</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Nume complet *</Label>
                  <Input
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="Dr. Ion Popescu"
                    required
                    data-testid="new-user-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="email"
                      className="pl-10"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="doctor@cabinet.ro"
                      required
                      data-testid="new-user-email"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Parolă *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="password"
                      className="pl-10"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      data-testid="new-user-password"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Rol *</Label>
                  <Select 
                    value={formData.role}
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger data-testid="new-user-role">
                      <SelectValue placeholder="Selectați rolul" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="medic">Medic</SelectItem>
                      <SelectItem value="asistent">Asistent</SelectItem>
                      <SelectItem value="admin">Administrator</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Anulează
                  </Button>
                  <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600" data-testid="save-new-user-btn">
                    Creează Utilizator
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Nu există utilizatori</p>
            </div>
          ) : (
            <div className="space-y-3">
              {users.map((u) => (
                <div
                  key={u.id}
                  className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl"
                  data-testid={`user-${u.id}`}
                >
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <User className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-slate-900">{u.full_name}</p>
                      {u.id === user.id && (
                        <Badge variant="outline" className="text-xs">Tu</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">{u.email}</p>
                  </div>
                  
                  <Badge className={getRoleBadgeColor(u.role)}>
                    {getRoleLabel(u.role)}
                  </Badge>
                  
                  {/* Actions - Edit pentru toți, Delete doar pentru alții */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"
                      onClick={() => openEditDialog(u)}
                      data-testid={`edit-user-${u.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    {u.id !== user.id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-slate-400 hover:text-red-500 hover:bg-red-50"
                        onClick={() => confirmDelete(u)}
                        data-testid={`delete-user-${u.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Edit User Dialog - Admin only */}
      {user?.role === 'admin' && (
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingUser?.id === user.id ? 'Editează Profilul Tău' : 'Editează Utilizator'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditUser} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Nume complet *</Label>
              <Input
                value={editFormData.full_name}
                onChange={(e) => setEditFormData({ ...editFormData, full_name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="email"
                  className="pl-10"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Parolă nouă (opțional)</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="password"
                  className="pl-10"
                  value={editFormData.password}
                  onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                  placeholder="Lăsați gol pentru a păstra parola actuală"
                  minLength={6}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Rol *</Label>
              <Select 
                value={editFormData.role}
                onValueChange={(value) => setEditFormData({ ...editFormData, role: value })}
                disabled={editingUser?.id === user.id}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="medic">Medic</SelectItem>
                  <SelectItem value="asistent">Asistent</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
              {editingUser?.id === user.id && (
                <p className="text-xs text-slate-500">Nu puteți schimba propriul rol</p>
              )}
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Anulează
              </Button>
              <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600">
                Salvează Modificările
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      )}

      {/* System Info - Admin only */}
      {user?.role === 'admin' && (
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Informații Sistem</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Versiune</p>
              <p className="font-medium">DiabetoCare v1.0.0</p>
            </div>
            <div>
              <p className="text-slate-500">Mediu</p>
              <p className="font-medium">Producție</p>
            </div>
            <div>
              <p className="text-slate-500">Bază de date</p>
              <p className="font-medium">MongoDB</p>
            </div>
            <div>
              <p className="text-slate-500">API</p>
              <p className="font-medium">FastAPI</p>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Delete Confirmation Dialog - Admin only */}
      {user?.role === 'admin' && (
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ștergeți utilizatorul?</AlertDialogTitle>
            <AlertDialogDescription>
              Sigur doriți să ștergeți utilizatorul <strong>{userToDelete?.full_name}</strong>? 
              Această acțiune este ireversibilă și va elimina definitiv contul din sistem.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulează</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteUser}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-user"
            >
              Șterge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      )}
    </div>
  );
};

export default SettingsPage;
